import { useRef, useCallback, useEffect } from 'react';
import { pb } from '@/utils/pocketbase';

interface UseGenerationPollingOptions {
  collection: string;
  intervalMs?: number;
  maxDurationMs?: number;
  onCompleted: (record: any) => void;
  onFailed?: (record: any) => void;
  onTimeout?: () => void;
}

export function useGenerationPolling({
  collection,
  intervalMs = 5000,
  maxDurationMs = 600000,
  onCompleted,
  onFailed,
  onTimeout,
}: UseGenerationPollingOptions) {
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
  }, []);

  const startPolling = useCallback(
    (recordId: string) => {
      stopPolling();

      pollIntervalRef.current = setInterval(async () => {
        try {
          const record = await pb.collection(collection).getOne(recordId);
          if (record.status === 'completed') {
            stopPolling();
            onCompleted(record);
          } else if (record.status === 'failed') {
            stopPolling();
            onFailed?.(record);
          }
        } catch {
          // Ignore poll errors, keep trying
        }
      }, intervalMs);

      pollTimeoutRef.current = setTimeout(() => {
        stopPolling();
        onTimeout?.();
      }, maxDurationMs);
    },
    [collection, intervalMs, maxDurationMs, onCompleted, onFailed, onTimeout, stopPolling],
  );

  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  return { startPolling, stopPolling };
}
